// Какво казва и какво позволява панелът „Плащане" в документа на доставка —
// чиста функция (тест в deliveryPaymentState.test.ts). Действията тук са
// „може ли по смисъл"; дали изобщо се показват бутони (документът е отворен от
// ред) решава модалът.
//
// • платена → „Платена на 12.09 · Банков превод · осн. … · отбеляза Мария" + Промени · Отбележи като неплатена
// • неплатена → „Неплатена · от N дни" + Отбележи като платена
// • заварена (отпреди плащането) → „Без данни за плащане" + Отбележи като платена
// • стокова във фактура → „Плаща се през фактурата" (+ „платена отделно на …"), без бутони
// • анулирана → само текст
import dayjs, { type Dayjs } from "dayjs";
import type { Delivery, DeliveryPaymentMethod } from "../../../../api/inventory";
import { isDeliveryAnnulled } from "./deliveryMeta";

export type PaymentPanelKind = "paid" | "unpaid" | "untracked" | "byInvoice" | "annulled";

export type PaymentPanelState = {
  kind: PaymentPanelKind;
  paidAt: string | null;
  method: DeliveryPaymentMethod | null;
  reference: string | null;
  paidByName: string | null;
  // неплатена: от колко дни (по датата на документа)
  ageDays: number;
  canMarkPaid: boolean;
  canChange: boolean;
  canMarkUnpaid: boolean;
};

const kindOf = (delivery: Delivery): PaymentPanelKind => {
  if (isDeliveryAnnulled(delivery)) return "annulled";
  switch (delivery.paymentStatus) {
    case "paid":
      return "paid";
    case "unpaid":
      return "unpaid";
    case "by_invoice":
      return "byInvoice";
    default:
      return "untracked";
  }
};

// null — BE без плащане (няма `paymentStatus`): панелът не се показва
export const paymentPanelOf = (
  delivery: Delivery,
  today: Dayjs = dayjs(),
): PaymentPanelState | null => {
  if (delivery.paymentStatus === undefined) return null;
  const kind = kindOf(delivery);
  return {
    kind,
    paidAt: delivery.paidAt ?? null,
    method: delivery.paymentMethod ?? null,
    reference: delivery.paymentReference?.trim() || null,
    paidByName: delivery.paidByName ?? null,
    ageDays: Math.max(0, today.startOf("day").diff(dayjs(delivery.date).startOf("day"), "day")),
    canMarkPaid: kind === "unpaid" || kind === "untracked",
    canChange: kind === "paid",
    canMarkUnpaid: kind === "paid",
  };
};

// Началните стойности на модала „Плащане" при „Промени": досегашното плащане
export const paymentInitialOf = (delivery: Delivery) =>
  delivery.paymentStatus === "paid" && delivery.paidAt
    ? {
        paidAt: dayjs(delivery.paidAt),
        method: delivery.paymentMethod ?? "bank",
        reference: delivery.paymentReference ?? "",
      }
    : undefined;
